import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type DeadlineDraft,
  type DeadlineErrorCode,
  draftDuration,
  rangeKind,
} from '@/shared/lib/deadline-draft';

import { Button } from './Button';
import styles from './DeadlineFields.module.css';
import { OverdueToggle } from './OverdueToggle';
import { RangeGroups, type RangeValue } from './RangeGroups';

interface Props {
  value: DeadlineDraft;
  onChange: (next: DeadlineDraft) => void;
  /** Problems to show (the caller validates with `validateRange`). */
  errors: readonly DeadlineErrorCode[];
  /** Profile time zone the values are read in (hint only). */
  timezone: string | null;
}

/**
 * Start and End together, always. Nothing set = no date; End only = a task with a deadline;
 * Start + End = an event (with its calculated length and the small "overdue after the end"
 * button); Start only is an error. Controlled and button-less: the card dialog autosaves valid
 * values, the create dialog reads the draft on Create.
 */
export function DeadlineFields({ value, onChange, errors, timezone }: Props) {
  const { t } = useTranslation();
  const id = useId();
  const kind = rangeKind(value);
  const has = (...codes: DeadlineErrorCode[]) => codes.some((code) => errors.includes(code));
  const set = (patch: Partial<DeadlineDraft>) => {
    const next = { ...value, ...patch };
    onChange({ ...next, hasStart: next.startDate !== '' });
  };
  const errorId = `${id}-errors`;
  const anything = kind !== 'none';

  return (
    <div className={styles.root}>
      <RangeGroups
        value={value}
        timeOptional
        describedBy={errorId}
        duration={kind === 'event' && errors.length === 0 ? draftDuration(value) : null}
        invalid={{
          startDate: has('startRequired', 'startDateInvalid', 'endBeforeStart'),
          startTime: has('startTimeInvalid'),
          endDate: has('endRequired', 'endDateInvalid', 'endBeforeStart'),
          endTime: has('endTimeInvalid'),
        }}
        onChange={(patch: Partial<RangeValue>) => {
          set(patch);
        }}
      />
      {errors.length > 0 ? (
        <p id={errorId} role="alert" className={styles.error}>
          {errors.map((code) => t(`deadline.errors.${code}`)).join(' ')}
        </p>
      ) : (
        <div className={styles.footer}>
          <p className={styles.hint} data-kind={kind}>
            {[
              t(`deadline.kind.${kind}`),
              timezone && anything ? t('deadline.zoneHint', { zone: timezone }) : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {kind === 'event' && (
            <OverdueToggle
              value={value.overdueEnabled === true}
              onChange={(overdueEnabled) => {
                set({ overdueEnabled });
              }}
            />
          )}
        </div>
      )}
      {anything && (
        <div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              onChange({
                hasStart: false,
                startDate: '',
                startTime: '',
                endDate: '',
                endTime: '',
                overdueEnabled: false,
              });
            }}
          >
            {t('deadline.clear')}
          </Button>
        </div>
      )}
    </div>
  );
}
